import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, LinearProgress, Alert, Chip
} from '@mui/material';
import { Refresh as RefreshIcon, ArrowForward as ArrowIcon } from '@mui/icons-material';
import { ACCOUNTING_MODULES } from './config/accountingModules';
import { getAccountingDashboard } from '../../services/api';
import { isAdmin, getUserRole } from '../../utils/auth';

const AccountingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const userRole = getUserRole();

  const visibleModules = ACCOUNTING_MODULES.filter(
    (m) => isAdmin() || m.roles.includes(userRole)
  );

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAccountingDashboard();
      setStats(res.data || res);
    } catch (err) {
      setError(err.message || 'Failed to load accounting dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Chart of Accounts', value: stats.totalAccounts, color: '#0891b2' },
    { label: 'Journal Entries', value: stats.totalJournalEntries, color: '#6366f1' },
    { label: 'Vendors', value: stats.totalVendors, color: '#dc2626' },
    { label: 'Bank Balance', value: stats.bankBalanceFormatted || stats.bankBalance, color: '#7c3aed' },
    { label: 'Unpaid Bills', value: stats.unpaidBillsFormatted || stats.unpaidBillsAmount, color: '#ea580c' },
    { label: 'Fixed Assets', value: stats.totalAssets, color: '#0d9488' },
    { label: 'Customers (AR)', value: stats.totalCustomers, color: '#16a34a' },
    { label: 'Open Periods', value: stats.openPeriods, color: '#2563eb' }
  ] : [];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>💼 Accounting Management</Typography>
          <Typography color="text.secondary">Complete financial management system for your business</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchStats}>Refresh</Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error} — Run backend/database/accounting_system.sql to set up tables.</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={4} md={3} key={card.label}>
              <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h5" fontWeight={700}>{card.value ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>Accounting Modules ({visibleModules.length})</Typography>
      <Grid container spacing={2}>
        {visibleModules.map((mod) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={mod.id}>
            <Card
              sx={{
                height: '100%', cursor: 'pointer', transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
              }}
              onClick={() => navigate(mod.path)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2, bgcolor: `${mod.color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22
                  }}>
                    {mod.icon}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>{mod.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                  {mod.features[0]}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`${mod.subPages.length} pages`} size="small" variant="outlined" />
                  <ArrowIcon fontSize="small" color="action" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AccountingDashboard;
